/**
 * Ccalendarview Component
 * Static mini-calendar grid for a single month, highlighting a target date
 */
'use client';
import { useMemo } from 'react';
import { Ctext } from '../Ctext';
import { ColorType, getColorValue } from '@/theme/colors';
import { FontWeight } from '@/theme/typography';
const LEADING_BLANK_VALUE = "leadingBlank";
const MONTH_DAY_VALUE = "monthDay";
const WEEKDAY_LABELS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
const MONTHS_LONG = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
];
type CalendarCell = {
    kind: 'leadingBlank';
    weekdayLabel: string;
} | {
    kind: 'monthDay';
    day: number;
};
export interface CcalendarviewProps {
    date: Date;
}
export function Ccalendarview({ date }: CcalendarviewProps) {
    const { year, month, targetDay, firstDayOfWeek, daysInMonth } = useMemo(() => {
        const y = date.getFullYear();
        const m = date.getMonth();
        return {
            year: y,
            month: m,
            targetDay: date.getDate(),
            firstDayOfWeek: new Date(y, m, 1).getDay(),
            daysInMonth: new Date(y, m + 1, 0).getDate(),
        };
    }, [date]);
    const cells = useMemo(() => {
        const leadingBlanks: CalendarCell[] = Array.from({ length: firstDayOfWeek }, (_, weekdayOffset) => ({
            kind: LEADING_BLANK_VALUE,
            weekdayLabel: WEEKDAY_LABELS[weekdayOffset],
        }));
        const monthDays: CalendarCell[] = Array.from({ length: daysInMonth }, (_, dayOffset) => ({
            kind: MONTH_DAY_VALUE,
            day: dayOffset + 1,
        }));
        return [...leadingBlanks, ...monthDays];
    }, [firstDayOfWeek, daysInMonth]);
    return (
      <div>
        <Ctext size={12} weight={FontWeight.SemiBold} color={ColorType.White} opacity={0.6} style={{ marginBottom: 8, display: 'block' }}>
          {MONTHS_LONG[month]} {year}
        </Ctext>
        <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(7, 26px)',
            rowGap: 1,
        }}>
          {WEEKDAY_LABELS.map((label) => (<div key={label} style={{
                width: 26,
                height: 20,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
            }}>
              <Ctext size={10} weight={FontWeight.Regular} color={ColorType.White} opacity={0.35}>
                {label}
              </Ctext>
            </div>))}
          {cells.map((cell) => {
            if (cell.kind === LEADING_BLANK_VALUE) {
                return (<div key={`leadingBlank-${cell.weekdayLabel}`} style={{ width: 26, height: 26 }}/>);
            }
            const isTarget = cell.day === targetDay;
            const dateIdentity = `${year}-${MONTHS_LONG[month]}-${cell.day}`;
            return (<div key={cell.day} style={{
                    width: 26,
                    height: 26,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    position: 'relative',
                }}>
                {isTarget && (<span aria-hidden="true" style={{
                        position: 'absolute',
                        inset: 2,
                        borderRadius: 999,
                        backgroundColor: getColorValue(ColorType.Accent, 0.18),
                        border: `1px solid ${getColorValue(ColorType.Accent, 0.45)}`,
                    }}/>)}
                <Ctext size={11} weight={isTarget ? FontWeight.SemiBold : FontWeight.Regular} color={isTarget ? ColorType.Accent : ColorType.White} opacity={isTarget ? 1 : 0.6} style={{ position: 'relative', zIndex: 1 }}>
                  {String(cell.day)}
                </Ctext>
              </div>);
        })}
        </div>
      </div>
    );
}
